import { EventObject, assign, send } from 'xstate';

import {
  getLaunchStrategyByParams,
  shouldUseModalForMobile,
} from '@/components/LaunchStrategiesProvider/utils';
import config from '@/config';
import { controllersEnum } from '@/logic/controllers';
import {
  COUNTRY_CODE_COOKIE_KEY,
  CRM_COOKIES_CONSENT_KEY,
  GAMING_SEGMENTS_COOKIE_NAME,
  HOMEPAGE_BASE_PATH,
  IDLE_POPUP_LONG_PAUSE_COOKIE_KEY,
  IDLE_POPUP_SHORT_PAUSE_COOKIE_KEY,
  TRANSPARENT_FAVICON_URL,
} from '@/logic/main/constants';
import { JackpotFeedsData } from '@/logic/main/services/fetchJackpotFeeds';
import {
  AuthTokenEvent,
  GameLaunchEvent,
  GenericEvent,
  IdlePopupBonusPermissionEvent,
  IdlePopupGameLaunchEvent,
  IdlePopupRecommendedGamesEvent,
  LogicContext,
  NavigationClickEvent,
  PageSeo,
  PromoClickEvent,
  SearchClickEvent,
  SearchSubheaderClickEvent,
  SegmentsEvent,
  UpdateBingoGamesEvent,
  UpdateJackpotsEvent,
  UpdateJackpotsV2Event,
  UpdatePersonalizedBannersEvent,
  UpdateRecentlyPlayedEvent,
  UpdateTablesEvent,
} from '@/logic/main/types';
import {
  delay,
  getCloseStrategyByParams,
  getPreviousPageArray,
  getPromotionsPath,
  isTrackingDelayed,
  isTrackingDisabled,
  isXsellVariant,
  managePhrases,
  mergeObjectDepthOne,
  omitKeys,
  shouldCloseExternally,
  toEachWordFirstUpperCase,
} from '@/logic/main/utils';
import * as krakenModels from '@/model/config/krakenModels';
import { promotionsModel } from '@/model/promotions';
import { getCanonicalUrl } from '@/model/seo';
import {
  installTracking,
  resetGameTrackingStorage,
  saveInitialTrackingData,
  saveSiteSpecificTrackingInfo,
  sendTrackEvents,
  trackBannerStandaloneClick,
  trackDepositCTAFrameClick,
  trackFloatingIcon,
  trackGameClick,
  trackGameClickFromLauncher,
  trackIdlePopup,
  trackIdlePopupGame,
  trackIdlePopupHideForSevenDays,
  trackMenuClick,
  trackOpenSearch,
  trackPartialEvent,
  trackPendingAccountEvent,
  trackPromoClick,
  trackSearchResult,
  trackShuffleClick,
  trackStepClick,
  trackTrebuchetPartialEvent,
  trackViewChange,
  updateGameTrackingStorage,
  updateLoginStatus,
  updateUserAccountNumber,
  updateUserBalance,
  updateUserCurrency,
} from '@/model/tracking';
import Cookies from '@/ports-frontend/Cookie';
import Document from '@/ports-frontend/Document';
import Footer from '@/ports-frontend/Footer';
import FullScreenFrontend from '@/ports-frontend/FullScreen';
import LivePersonChatFrontend from '@/ports-frontend/LivePersonChat';
import Location from '@/ports-frontend/Location';
import MessageBus from '@/ports-frontend/MessageBus';
import MyAccount from '@/ports-frontend/MyAccount';
import Scroll from '@/ports-frontend/Scroll';
import Window from '@/ports-frontend/Window';
import { BannerStandaloneItemClickParams } from '@/types/category/bannerStandaloneTypes';
import { PortalVerticalGameTilePropTypes } from '@/types/gameTypes';
import { JackpotsV2ResponseType } from '@/types/jackpotTypes';
import { getLocale } from '@/utils';
import { isHybrid888 } from '@/utils/isHybrid888';

import { gameLaunchActions } from './game';

async function getRecentPhrases(recentPhrases?: string[]) {
  try {
    if (!recentPhrases || !recentPhrases.length) return [];
    const recentPhrasesFromCookie = await Cookies.get('recentPhrases');
    if (!recentPhrasesFromCookie) return recentPhrases;

    return managePhrases(recentPhrases, JSON.parse(recentPhrasesFromCookie), 5);
  } catch (e) {
    console.error('Could not parse "recentPhrases" cookie.', e);
    return [];
  }
}

export const actions = {
  ...gameLaunchActions,

  saveScrollPosition: () => {
    Scroll.saveScrollPosition();
  },

  scrollTop: async (context: LogicContext, event: any) => {
    if (event?.data?.noScroll) {
      return;
    }
    /*
      Low latency helps prevent situations when scrolling
      takes place before the content container is loaded
     */
    await delay(50);
    Scroll.scrollTop();
  },

  closeApplication: () => {
    Window.postMessage('wh.xsell.command.gaming-lobby.close', '*');
  },

  handleOpenInIframe: () => {
    config.features.iFrame.mode === 'disabled' && Window.handleOpenInIframe();
  },

  sendTrackEvents: () => {
    sendTrackEvents();
  },

  // TRACKING
  installTracking: () => {
    if (isTrackingDisabled() || isTrackingDelayed()) {
      return;
    }
    installTracking();
  },

  updateGameLaunchedTracking: (context: LogicContext) =>
    updateGameTrackingStorage(context.gameToLaunch, context.deviceInfo),

  resetGameLaunchedTracking: () => resetGameTrackingStorage(),

  trackGameClick: (context: LogicContext, event: GameLaunchEvent) => {
    if (isTrackingDisabled()) {
      return;
    }
    trackGameClick({
      gameParams: event.payload,
      deviceInfo: context.deviceInfo,
    });
  },

  trackSearchGameClick: (context: LogicContext, event: GameLaunchEvent) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackGameClick({
      gameParams: {
        ...event.payload,
        searchSource: context.search?.sourceType,
      },
      deviceInfo: context.deviceInfo,
    });
  },

  trackGameClickFromLauncher: (
    context: LogicContext,
    event: GameLaunchEvent
  ) => {
    if (isTrackingDisabled() || !context.gameToLaunch) {
      return;
    }

    trackGameClickFromLauncher({
      game: context.gameToLaunch,
      deviceInfo: context.deviceInfo,
    });
  },

  trackPromoClick: (context: LogicContext, event: PromoClickEvent) => {
    if (
      isTrackingDisabled() ||
      !event.payload.isBanner ||
      event.payload.isPersonalizedBanner
    ) {
      return;
    }
    const mappedUrl =
      event.payload.url ||
      `${config.urls.app}${config.base_path}${event.payload.path}`;
    trackPromoClick(mappedUrl);
  },

  trackFloatingIcon: async () => {
    if (isTrackingDisabled()) {
      return;
    }

    trackFloatingIcon();
  },

  trackDepositClick: (context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackDepositCTAFrameClick();
  },

  trackPageChange: () => {
    if (isTrackingDisabled()) {
      return;
    }

    trackViewChange();
  },

  trackSearchResult: (context: LogicContext) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackSearchResult({
      search: context.search,
    });
  },

  trackOpenSearch: () => {
    if (isTrackingDisabled()) {
      return;
    }
    trackOpenSearch();
  },

  trackHamburgerClick: () => {
    if (isTrackingDisabled()) {
      return;
    }

    trackMenuClick({
      title: 'Menu',
      actionTitle: 'Burger Menu',
    });
  },

  trackErrorItemClick: (context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackMenuClick({
      title: event.payload.title || '',
      actionTitle: 'Error',
    });
  },

  trackMainMenuItemClick: (context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return;
    }
    const { sectionTitle, itemTitle, containerTitle } = event.payload;
    const actionTitle =
      containerTitle || (sectionTitle === 'Games' ? 'Main Menu' : sectionTitle);

    trackMenuClick({
      title: itemTitle,
      actionTitle,
    });
  },

  trackSideItemClick: (context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return;
    }
    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
    });
  },

  trackBottomNavigationClick: (
    context: LogicContext,
    event: { payload: { itemTitle: string } }
  ) => {
    if (isTrackingDisabled()) {
      return;
    }
    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
      actionTitle: 'Bottom Nav',
    });
  },

  trackMenuItemClick: (
    context: LogicContext,
    event: { payload: { pageId: string; itemTitle: string } }
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
    });
  },

  trackStepItemClick: (
    context: LogicContext,
    event: { payload: { stepTitle: string } }
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    const { stepTitle } = event.payload;

    trackStepClick({
      title: stepTitle,
    });
  },

  trackBannerStandaloneItemClick: (
    context: LogicContext,
    event: { payload: BannerStandaloneItemClickParams }
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackBannerStandaloneClick(event.payload);
  },

  trackShuffleGamesClick: (
    context: LogicContext,
    event: { payload: { isGameShuffleStartAction: boolean } }
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    const isGameShuffleStartAction = event.payload.isGameShuffleStartAction;

    trackShuffleClick({ isGameShuffleStartAction });
  },

  trackIdlePopupGameTileClick: (
    context: LogicContext,
    event: IdlePopupGameLaunchEvent
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackGameClick({
      gameParams: {
        ...event.payload,
      },
      deviceInfo: context.deviceInfo,
    });
  },

  trackIdlePopupPlayBtnClick: (
    context: LogicContext,
    event: IdlePopupGameLaunchEvent
  ) => {
    if (isTrackingDisabled()) {
      return;
    }

    trackGameClick({
      gameParams: {
        ...event.payload,
      },
      deviceInfo: context.deviceInfo,
    });
  },

  trackIdlePopupHideForSevenDays: async () => {
    if (isTrackingDisabled()) {
      return;
    }
    const idlePopupLongPauseCookie = await Cookies.get(
      IDLE_POPUP_LONG_PAUSE_COOKIE_KEY
    );

    if (idlePopupLongPauseCookie) {
      trackIdlePopupHideForSevenDays();
    }
  },

  trackIdlePopupComponent: (context: LogicContext) => {
    if (isTrackingDisabled()) {
      return;
    }

    if (
      context.idlePopupComponent?.enabled &&
      context.idlePopupComponent?.idlePopupGame
    ) {
      trackIdlePopup();
      trackIdlePopupGame({ game: context.idlePopupComponent?.idlePopupGame });
    }
  },

  setInitialMainConfigValues: assign((context: LogicContext) => {
    return { ...context, isHybridApplication: isHybrid888() };
  }),

  updateLoginCount: assign(
    (context: LogicContext, event: EventObject & { data: number }) => {
      return { ...context, loginCount: event.data };
    }
  ),

  updateIdlePopupGame: assign((context: LogicContext) => {
    const { recommendedGames = [] } = context;

    if (!recommendedGames.length) return { ...context };

    const randomIndex = Math.floor(Math.random() * recommendedGames.length);
    const randomGame = recommendedGames[randomIndex];

    return {
      ...context,
      idlePopupComponent: {
        ...context.idlePopupComponent,
        idlePopupGame: randomGame,
      },
    };
  }),

  updateIdlePopupCookie: assign((context: LogicContext, event: any) => {
    return {
      ...context,
      idlePopupComponent: {
        ...context.idlePopupComponent,
        cookieTimestamp: event.data?.idleCookieSp,
        isLongPauseActive: Boolean(event.data?.idleCookieLp),
      },
    };
  }),

  changeIdlePopupStatusToEnabeld: assign((context: LogicContext) => {
    return {
      ...context,
      idlePopupComponent: {
        ...context.idlePopupComponent,
        enabled: true,
      },
    };
  }),

  changeIdlePopupStatusToDisabled: assign((context: LogicContext) => {
    return {
      ...context,
      idlePopupComponent: {
        ...context.idlePopupComponent,
        enabled: false,
      },
    };
  }),

  hideIdlePopupSetCookie: (context: LogicContext) => {
    const pauseTimeInterval =
      Number(context?.idlePopupComponent?.longPauseTimeInterval) || 60; // default 60min
    const timestamp = new Date().getTime() + pauseTimeInterval * 60 * 1000;
    const expirationTime = new Date(timestamp);

    Cookies.set(IDLE_POPUP_SHORT_PAUSE_COOKIE_KEY, timestamp, {
      expires: expirationTime,
    });
  },

  udpateIdlePopoupLongPauseCookie: (
    context: LogicContext,
    event: { payload: { checked?: boolean } }
  ) => {
    if (event.payload?.checked) {
      Cookies.set(IDLE_POPUP_LONG_PAUSE_COOKIE_KEY, 'true', { expires: 7 }); // 7 days
    } else {
      Cookies.remove(IDLE_POPUP_LONG_PAUSE_COOKIE_KEY);
    }
  },

  trackTrebuchetPartialEvent: (context: LogicContext, event: any) => {
    const { event: name, payload } = event.payload;
    trackTrebuchetPartialEvent(name, payload);
  },

  trackPartialEvent: assign((context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return context;
    }
    const { event: name, payload } = event.payload;

    // prevent Kraken from overwriting data controlled by Gaming
    const payloadPartialEvent = omitKeys(
      [
        'LandingDocumentLocation',
        'PageUrl',
        'PageTitle',
        'SiteLanguage',
        'SiteSection',
      ],
      payload
    );
    const pendingAccountEvent = trackPartialEvent({
      name,
      payloadPartialEvent,
      isAccountInfoAvailable: context.isAccountInfoAvailable,
    });
    if (!pendingAccountEvent) {
      return context;
    }

    return {
      ...context,
      isAccountInfoAvailable: false,
      pendingAccountEvents: [
        ...context.pendingAccountEvents,
        pendingAccountEvent,
      ],
    };
  }),

  saveInfoAccountInfo: assign((context: LogicContext) => {
    return {
      ...context,
      isAccountInfoAvailable: true,
    };
  }),

  trackPendingAccountEvents: assign((context: LogicContext) => {
    const { pendingAccountEvents = [], segments } = context;
    pendingAccountEvents.forEach((event: any) => {
      trackPendingAccountEvent({
        ...event,
        segments,
      });
    });
    return {
      ...context,
      pendingAccountEvents: [],
    };
  }),

  logEvent: (context: LogicContext, event: any) => {
    console.log('EVENT ', event);
  },

  saveInitialTrackingData: () => {
    if (isTrackingDisabled()) {
      return;
    }

    saveInitialTrackingData();
  },

  updateSiteTrackingStorageInfo: (context: LogicContext, event: any) => {
    if (isTrackingDisabled()) {
      return;
    }

    if (!event.data?.trackingData) return;

    saveSiteSpecificTrackingInfo(
      event.data.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
  },

  updateUserTrackingInfo: (context: LogicContext) => {
    updateLoginStatus(context.user.loggedIn);
    updateUserAccountNumber(context.user.userAccountNumber);
    updateUserBalance(context.user.balance);
    updateUserCurrency(context.user.currency);
  },

  updateSeo: async (
    context: LogicContext,
    event?: { data?: { seo?: { page?: PageSeo } } }
  ) => {
    const seo = event?.data?.seo?.page;

    if (!seo) return;
    const canonicalUrl = await getCanonicalUrl(seo.canonicalLinks[0]);

    Document.setTitle(seo.title);
    Document.setMetaDescription(seo.description);
    const noIndex = config.disallowIndexing?.enabled ? true : seo.noIndex;
    Document.manageRobotsTag(noIndex, seo.noTranslate);
    Document.setCanonicalTag(canonicalUrl);
    Document.setSchemaMarkUp(seo.structuredData);
    Document.removeHreflangTags(seo.canonicalLinks[0]);

    Document.OpenGraph.setDescription(seo.description);
    Document.OpenGraph.setTitle(seo.title);
    Document.OpenGraph.setURL(canonicalUrl);
    Document.OpenGraph.setType('website');
    Document.OpenGraph.setImage(seo.image || TRANSPARENT_FAVICON_URL);

    Document.TwitterCards.setDescription(seo.description);
    Document.TwitterCards.setTitle(seo.title);
    Document.TwitterCards.setURL(canonicalUrl);
    Document.TwitterCards.setType('summary');
    Document.TwitterCards.setImage(seo.image || TRANSPARENT_FAVICON_URL);
  },

  openUsabilla: () => {
    Window.openUsabilla();
  },

  setBodyNonScrollable: () => {
    Document.setBodyTopPosition(true);
    Document.setBodyNonScrollable();
  },

  setBodyNonScrollableDefaultTopPosition: () => {
    Document.setBodyTopPosition(false);
    Document.setBodyNonScrollable();
  },

  setBodyScrollable: () => {
    Document.setBodyScrollable();
  },

  scrollTopAndSetBodyNonScrollable: () => {
    Scroll.scrollTop();
    Document.setBodyNonScrollable();
  },

  sendGameModalCloseToGameFrame: () => {
    MessageBus.publishEvent({
      topic: 'wh.trebuchet.command.ui.game.close',
    });
  },

  // GT-2095 - cookie with user country code required for proper registration initialization
  setCookieUserCountry: async (context: LogicContext) => {
    const assemblerContext = await Window.getAssemblerContext();
    await Cookies.set(
      COUNTRY_CODE_COOKIE_KEY,
      assemblerContext?.viewer_country || '',
      {
        expires: 365,
      }
    );
  },

  // Footer
  hideFooter: () => {
    Footer.hide();
  },

  showFooter: () => {
    Footer.show();
  },

  //Location
  updateUrl: (context: LogicContext) => {
    const pathToUpdate = context.currentPagePath;
    Location.set(pathToUpdate);
  },

  // Header Product Navigation
  trackRedirectToProduct: (
    context: LogicContext,
    event: {
      type: string;
      data: {
        label: string;
        link: {
          url: string;
        };
      };
    }
  ) => {
    if (event.type === 'VIEW_HEADER_PRODUCT_EVENT_CALLED') {
      if (!isTrackingDisabled()) {
        trackMenuClick({
          title: event.data?.label,
          actionTitle: 'Top Nav',
        });
      }
    }
  },

  initiateRedirectWithDelay: send(
    (
      context: LogicContext,
      event: {
        type: string;
        data: {
          label: string;
          link: {
            url: string;
          };
        };
      }
    ) => ({
      type: 'REDIRECT_TO_PRODUCT',
      data: event.data,
    }),
    { delay: 300 }
  ),

  redirectToProductPage: (
    context: LogicContext,
    event: {
      type: string;
      data: {
        label: string;
        link: {
          url: string;
        };
      };
    }
  ) => {
    if (event.type === 'REDIRECT_TO_PRODUCT' && event.data?.link?.url) {
      Window.redirectToUrl(event.data.link.url);
    }
  },

  openPromotionsPage: (
    context: LogicContext,
    event: { payload: { path: string } }
  ) => {
    const path = event.payload.path;
    path && Location.set(path);
  },

  updateBalance: () => {
    MessageBus.publishEvent({ topic: 'refreshBalance' });
  },

  updatePromotionsPath: (
    context: LogicContext,
    event: { pageSlug?: string }
  ) => {
    const path = getPromotionsPath(event.pageSlug);

    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version ? path.replace(`${config.version}`, '') : path,
      context.segments
    );

    const pathToUpdate = path;
    pathToUpdate && Location.set(pathToUpdate);
  },

  resetSearchAttempts: assign<LogicContext>({
    search: (context: LogicContext) => ({
      ...context.search,
      attempts: 0,
    }),
  }),

  updateSearchAttempts: assign<LogicContext>({
    search: (context: LogicContext) => ({
      ...context.search,
      attempts: context.search.attempts ? context.search.attempts + 1 : 1,
    }),
  }),

  saveSearchInputType: assign<LogicContext>({
    search: (context: LogicContext, event: EventObject & { payload?: any }) => {
      return {
        ...context.search,
        sourceType: event.payload.sourceType,
      };
    },
  }),

  handleSubnavigationScrollReset: assign(
    (context: LogicContext, event: NavigationClickEvent) => {
      const path: string = event?.payload?.path || '';
      return {
        ...context,
        subnavigationScrollPosition:
          path.lastIndexOf('/') === path.indexOf('/') ? 0 : undefined,
      };
    }
  ),

  sendAlgoliaAnalytics: async (
    context: LogicContext,
    event: GameLaunchEvent
  ) => {
    const { default: searchInsights } = await import(
      /* webpackChunkName: "search-insights" */ 'search-insights'
    );
    const { userToken, queryID } = context.search;
    const { gameId, gamePosition } = event.payload;
    const basicObject = {
      userToken,
      eventName: `searched_game_launch`,
      index: config.algolia.config.index,
      queryID,
      objectIDs: [gameId],
    };

    searchInsights.clickedObjectIDsAfterSearch({
      ...basicObject,
      positions: [gamePosition],
    });

    searchInsights.convertedObjectIDsAfterSearch(basicObject);
  },

  // CATEGORIES
  resetPageAction: assign((context: LogicContext, event: any) => {
    if (context.changePageAction === 'BACK') {
      return {
        ...context,
        changePageAction: '',
        restoreScroll: true,
      };
    }
    return {
      ...context,
    };
  }),

  setScrollRestore: assign(() => ({
    restoreScroll: true,
  })),

  clearScrollRestore: assign(() => ({
    restoreScroll: false,
  })),

  restoreScrollPosition: () => {
    Scroll.restoreScrollPosition();
  },

  savePreviousUser: assign((context: LogicContext) => {
    return {
      context,
      previousUserAccountNumber: context.user.userAccountNumber,
    };
  }),

  updateCurrentPagePath: assign((context: LogicContext, event: any) => {
    let pagePath = event.payload?.path || event.data?.path;

    if (event.payload?.pageSlug) {
      pagePath = getPromotionsPath(event.payload?.pageSlug);
    }

    return pagePath
      ? {
          ...context,
          mainLayoutPath: pagePath,
          currentPagePath: pagePath,
        }
      : context;
  }),

  updatePrevPageArray: assign((context: LogicContext, event: any) => {
    const pagePath = event.payload?.path || event.data?.path;

    return {
      ...context,
      previousPageArray:
        context.currentPagePath !== pagePath
          ? getPreviousPageArray({
              pageParams: {
                pagePath: context.currentPagePath,
                trackingData: context.trackingData?.page,
              },
              previousPageArray: context.previousPageArray,
              arraySizeLimit: 10,
            })
          : context.previousPageArray,
    };
  }),

  removeLastPagePath: assign((context: LogicContext) => {
    const basePath = config.base_path || HOMEPAGE_BASE_PATH;

    if (context.previousPageArray) {
      const reversedPageArray = [...context.previousPageArray]
        .slice()
        .reverse();
      return {
        ...context,
        currentPagePath:
          reversedPageArray.find(
            page => page.pagePath !== context.currentPagePath
          )?.pagePath || basePath,
        previousPageArray: context.previousPageArray.slice(0, -1),
      };
    }

    return {
      ...context,
      currentPagePath: basePath,
    };
  }),

  removeLastPreviousPagePath: assign((context: LogicContext) => {
    if (context.previousPageArray && context.previousPageArray.length > 1) {
      return {
        ...context,
        previousPageArray: context.previousPageArray.slice(0, -1),
      };
    }

    return {
      ...context,
    };
  }),

  logger: (context: LogicContext, event: any) => {
    console.log({ context, event });
  },
  _logger_event: (context: LogicContext, event: any) => {
    console.log(JSON.stringify(event, null, 2));
  },

  setHeaderInitialized: assign((context: LogicContext) => ({
    ...context,
    headerInitialized: true,
  })),

  // SEARCH
  savePhrase: async (context: LogicContext) => {
    if (!context.user.loggedIn) return;

    const phrases = await getRecentPhrases(context.recentPhrases);
    await Cookies.set('recentPhrases', JSON.stringify(phrases));
  },

  prepareSearchInputConfig: assign((context: LogicContext) => {
    const { deviceInfo } = context;
    const isIOS = deviceInfo.nativeDeviceOs === 'ios';
    const isRenderedInIFrame = config.features.iFrame.mode === 'enabled';

    return {
      ...context,
      inputConfig: {
        isIOS,
        isRenderedInIFrame,
      },
    };
  }),

  setNumberOfAllSearchGames: assign((context: LogicContext) => {
    const { numberOfAllSearchGames } = context;

    return {
      ...context,
      search: {
        ...context.search,
        numberOfAllSearchGames,
      },
    };
  }),

  clearSearch: assign((context: LogicContext) => {
    return {
      ...context,
      search: {
        ...context.search,
        searchItems: [],
        searchPhrase: '',
        itemsNumber: null,
      },
    };
  }),

  showGamesOfMatchingType: assign(
    (context: LogicContext, event: SearchSubheaderClickEvent) => {
      const currentType: string = event.payload.type;
      const itemsNumber = context.search.searchItems.filter(
        i => i.type === currentType
      ).length;

      return {
        ...context,
        search: {
          ...context.search,
          currentType,
          itemsNumber,
        },
      };
    }
  ),

  updateCurrentPhrase: assign(
    (context: LogicContext, event: SearchClickEvent) => {
      const phrase = event.payload.value;
      return {
        ...context,
        search: {
          ...context.search,
          searchPhrase: toEachWordFirstUpperCase(phrase),
          itemsNumber: null,
        },
      };
    }
  ),

  updatePhrases: assign((context: LogicContext) => {
    const phrase = context.search?.searchPhrase;
    if (!phrase) return context;
    const preparedPhrase = toEachWordFirstUpperCase(phrase);

    let { recentPhrases, phrasePendingAdditionToRecentPhrases } = context;
    let phraseToAdd = phrasePendingAdditionToRecentPhrases;

    if (!preparedPhrase || preparedPhrase.length < 3) {
      return context;
    }
    if (!context.user.loggedIn) {
      return {
        ...context,
        phrasePendingAdditionToRecentPhrases: preparedPhrase,
      };
    }

    if (phraseToAdd) {
      recentPhrases = managePhrases(recentPhrases, phraseToAdd, 5);
      phraseToAdd = '';
    }

    return {
      ...context,
      recentPhrases: managePhrases(recentPhrases, preparedPhrase, 5),
      phrasePendingAdditionToRecentPhrases: phraseToAdd,
    };
  }),

  setCookieBannerCookie: async (context: LogicContext) => {
    await Cookies.set(CRM_COOKIES_CONSENT_KEY, '', {
      expires: 3600 * 24 * 365 * 10,
    });
  },

  // GAMES
  setSuggestedGameUrl: assign((context: LogicContext, event: any) => {
    if (context.gameToLaunch) {
      return {
        ...context,
        gameToLaunch: {
          ...context.gameToLaunch,
          triggeredByUser: true,
        },
      };
    }

    return context;
  }),

  updateJackpots: assign(
    (context: LogicContext, event: UpdateJackpotsEvent) => {
      const { multiJackpots } = event.data;
      return {
        ...context,
        multiJackpots: mergeObjectDepthOne(
          context.multiJackpots,
          multiJackpots
        ),
      };
    }
  ),

  updateTables: assign((context: LogicContext, event: UpdateTablesEvent) => {
    const { liveDealerTables } = event.data;

    return {
      ...context,
      currentTables: {
        ...liveDealerTables,
      },
    };
  }),

  updateJackpotBetFeeds: assign((context: LogicContext, event: any) => {
    const { data } = event.data;
    return {
      ...context,
      jackpotBetFeeds: data,
    };
  }),

  updateJackpotFeeds: assign(
    (context: LogicContext, event: GenericEvent<JackpotFeedsData>) => {
      const { data } = event.data;
      const jackpots = context.jackpots;

      for (const providerData of data) {
        const { provider, data } = providerData;

        const providerSlice = jackpots[provider];

        if (!providerSlice) {
          continue;
        }

        Object.entries(data).forEach(([key, jackpot]) => {
          const jackpotByCodeSlice = providerSlice[key];

          if (!jackpotByCodeSlice) {
            return;
          }

          jackpotByCodeSlice.fieldsValues = jackpot;
        });
      }
      return {
        ...context,
        jackpots: {
          ...context.jackpots,
        },
      };
    }
  ),

  updateJackpotsV2: assign(
    (context: LogicContext, event: UpdateJackpotsV2Event) => {
      const { jackpots } = event.data;
      const extendedJackpots: Record<string, JackpotsV2ResponseType> = {};
      for (const key in jackpots) {
        extendedJackpots[key] = {
          ...context?.currentJackpotsV2?.[key],
          ...jackpots[key],
        };
      }

      return {
        ...context,
        currentJackpotsV2: {
          ...context.currentJackpotsV2,
          ...extendedJackpots,
        },
      };
    }
  ),

  updateStrikeGoldData: assign((context: LogicContext, event: any) => {
    return {
      ...context,
      strikeGoldData: {
        ...event.data.strikeGold,
      },
    };
  }),

  updatePersonalizedBanners: assign(
    (context: LogicContext, event: UpdatePersonalizedBannersEvent) => {
      const { banners } = event.payload;

      if (!banners) {
        return context;
      }

      let mapped = {};

      banners.forEach(b => {
        mapped = {
          ...mapped,
          [b.container]: b.rendered,
        };
      });

      return {
        ...context,
        currentPersonalizedBanners: mapped,
      };
    }
  ),

  initAuthTokenRefresh: send((context: LogicContext, event: any) => {
    // After the auth token expires. Query once for a refreshed token.
    // If the second answer is incorrect from the Data Gateway, clear the recently played section.
    if (
      context.user.refreshTokenRetryNumber &&
      context.user.refreshTokenRetryNumber > 1
    ) {
      return { type: 'CLEAR_RECENTLY_PLAYED' };
    }

    return { type: 'FETCH_REFRESHED_AUTH_TOKEN_WITH_DEPENDENCIES' };
  }),

  updateRetryStatus: assign((context: LogicContext) => {
    return {
      ...context,
      user: {
        ...context.user,
        refreshTokenRetryNumber:
          context.user.refreshTokenRetryNumber &&
          Number.isInteger(context.user.refreshTokenRetryNumber)
            ? ++context.user.refreshTokenRetryNumber
            : 1,
      },
    };
  }),

  clearRecentlyPlayedGames: assign((context: LogicContext) => {
    return {
      ...context,
      user: {
        ...context.user,
        recentlyPlayedGamesList: [],
      },
    };
  }),

  updateRecentlyPlayedGames: assign(
    (context: LogicContext, event: UpdateRecentlyPlayedEvent) => {
      const { recentlyPlayedGamesList } = event.data;

      return {
        ...context,
        user: {
          ...context.user,
          refreshTokenRetryNumber: 0,
          recentlyPlayedGamesList,
        },
      };
    }
  ),

  saveFetchedBingoGames: assign(
    (context: LogicContext, event: UpdateBingoGamesEvent) => {
      const { bingoGames } = event.data;

      return { ...context, bingoGames };
    }
  ),

  saveBonusPermissionState: assign(
    (context: LogicContext, event: IdlePopupBonusPermissionEvent) => {
      const { status, data } = event.data;

      if (status !== 200) return { ...context };

      return {
        ...context,
        idlePopupComponent: {
          ...context.idlePopupComponent,
          isBonusPermission: !Boolean(data?.bonusBlocked),
        },
      };
    }
  ),

  saveRecommendedGames: assign(
    (context: LogicContext, event: IdlePopupRecommendedGamesEvent) => {
      const { status, recommendedGames = [] } = event.data;

      if (status !== 200 || !recommendedGames.length) return { ...context };

      return {
        ...context,
        recommendedGames,
      };
    }
  ),

  saveFetchedShuffleGames: assign((context: LogicContext, event: any) => {
    const { status, shuffleGames } = event.data;

    return {
      ...context,
      shuffleGames: {
        ...context.shuffleGames,
        status,
        availableList: shuffleGames,
      },
    };
  }),

  getShuffleGame: assign((context: LogicContext) => {
    let drawnList = context.shuffleGames.drawnList || [];

    const getRandomIndex = (array: PortalVerticalGameTilePropTypes[]) => {
      return Math.floor(Math.random() * array.length);
    };

    const getRandomGame = (
      games: PortalVerticalGameTilePropTypes[]
    ): PortalVerticalGameTilePropTypes | null => {
      if (games && games.length < 2) {
        return games[0] || null;
      }
      const randomIndex = getRandomIndex(games);
      const isDuplicated = drawnList.includes(randomIndex);

      if (isDuplicated) {
        return getRandomGame(games);
      }

      drawnList.push(randomIndex);

      if (games.length === drawnList.length) {
        const lastVal = drawnList[drawnList.length - 1];
        drawnList = [lastVal];
      }

      return games[randomIndex];
    };

    const drawnGame = getRandomGame(context.shuffleGames.availableList);

    return {
      ...context,
      shuffleGames: {
        ...context.shuffleGames,
        drawnList,
        drawnGame,
      },
    };
  }),

  clearShuffleGames: assign((context: LogicContext) => ({
    ...context,
    shuffleGames: {
      status: null,
      availableList: [],
      drawnList: [],
      drawnGame: null,
    },
  })),

  // PROMOTIONS
  showPromotionsList: (context: LogicContext) => {
    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
    promotionsModel.showPromotionsList(getLocale(config));
  },

  showPromotionDetails: (context: LogicContext) => {
    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
    const { components } = context.controllersData[controllersEnum.PROMOTIONS]!;
    const promotionDetails = components.find(
      ([id, entry]) => entry.properties.mode === 'details'
    )!;

    promotionsModel.showPromotionsDetails(
      promotionDetails[1].properties.slug,
      getLocale(config)
    );
  },

  // GAME LAUNCH

  setStatusGameModalForMobile: assign((context: LogicContext) => {
    const { deviceType } = context.deviceInfo;

    if (context.gameToLaunch && context.gameLaunchStrategies) {
      const launchStrategy = getLaunchStrategyByParams(
        context.gameToLaunch.integration,
        context.gameLaunchStrategies
      );

      return {
        ...context,
        isGameModalForMobileOpened: shouldUseModalForMobile(
          deviceType,
          launchStrategy
        ),
      };
    }

    return {
      ...context,
    };
  }),

  // SCREEN SIZE
  saveCurrentScreenSize: assign((context: LogicContext, event: any) => ({
    currentScreenSize: event.data.size,
  })),
  saveFullScreenMode: assign((context: LogicContext, event: any) => ({
    isFullScreen: event.data.isFullScreen,
  })),
  handleFullScreenOverlayClick: () => {
    FullScreenFrontend.toggleFullScreen();
  },
  setFullScreenOff: async () => {
    if (await FullScreenFrontend.isFullScreen()) {
      FullScreenFrontend.toggleFullScreen();
    }
  },

  // AUTH TOKEN
  saveAuthToken: assign((context: LogicContext, event: AuthTokenEvent) => {
    if (event.data?.access_token) {
      return {
        ...context,
        user: {
          ...context.user,
          authToken: {
            ...event.data,
            initialSetup:
              context.user.authToken?.initialSetup &&
              context.user.authToken?.initialSetup === true
                ? false
                : true,
          },
        },
      };
    }

    return {
      ...context,
    };
  }),

  // REFRESHED AUTH TOKEN
  saveRefreshedAuthToken: assign(
    (context: LogicContext, event: AuthTokenEvent) => {
      if (event.data?.access_token) {
        return {
          ...context,
          user: {
            ...context.user,
            authToken: {
              ...context.user.authToken,
              ...event.data,
              initialSetup: false,
            },
          },
        };
      }
      return {
        ...context,
      };
    }
  ),

  fetchAuthToken: send('FETCH_AUTH_TOKEN'),

  fetchRefreshedSessionToken: send('FETCH_REFRESHED_AUTH_TOKEN'),

  checkSegmentsCookie: assign(
    (context: LogicContext, event: SegmentsEvent) => ({
      ...context,
      segments: event.data?.segments ? event.data?.segments : null,
    })
  ),

  saveSegments: assign((context: LogicContext, event: SegmentsEvent) => {
    const { data } = event;
    const segments =
      Array.isArray(data?.segments) && data.segments.length
        ? data.segments
        : [];

    try {
      Cookies.set(
        GAMING_SEGMENTS_COOKIE_NAME,
        JSON.stringify({ segments: segments }),
        {
          expires: 1, // 1 day
        }
      );
    } catch (e) {
      console.error('Cookie Segments could not be saved.');
    }

    return {
      ...context,
      segments,
    };
  }),

  getJackpotsV2: send('GET_JACKPOTSV2'),
  getRecentlyPlayedJackpotV2: send('GET_RECENTLY_PLAYED_JACKPOTSV2'),
  getSearchGamesJackpotsV2: send('GET_SEARCH_GAMES_JACKPOTVS2'),
  refetchStaleJackpotsV2: send('REFETCH_STALE_JACKPOTSV2'),

  fetchRecentlyPlayed: send('UPDATE_RECENTLY_PLAYED'),
  fetchRecentlyPlayedAfterDelay: send('UPDATE_RECENTLY_PLAYED_AFTER_DELAY'),

  renderBingoGames: send('RENDER_BINGO_GAMES'),
  watchBingoGamesTimer: send('WATCH_BINGO_GAMES_TIMER'),
  watchJackpotBetFeeds: send('WATCH_JACKPOT_BET_FEEDS'),

  fetchShuffleGames: send(
    (context: LogicContext, event: EventObject & { payload?: any }) => {
      return {
        type: 'FETCH_SHUFFLE_GAMES',
        payload: event.payload,
      };
    }
  ),

  // OPEN DEPOSIT
  openDeposit: () => {
    krakenModels.openDeposit();
  },

  // USER BALANCE
  hideBalance: () => {
    krakenModels.hideBalance();
  },

  showBalance: () => {
    krakenModels.showBalance();
  },

  // CLOSE MY ACCOUNT DROPDOWN
  hideMyAccountDropdown: () => {
    krakenModels.hideMyAccountDropdown();
  },
  openSelfExclusion: async (
    _: LogicContext,
    event: EventObject & { payload: { iosRedirectUrl: string } }
  ) => {
    if (isXsellVariant()) {
      await MyAccount.openSelfExclusion(event.payload.iosRedirectUrl);
    } else {
      krakenModels.openSelfExclusion();
    }
  },

  enableZoom: () => Document.enableZoom(),
  disableZoom: () => Document.disableZoom(),

  /*------------------------SEND ACTIONS------------------------*/
  // GAME
  gameFetchFailed: send(
    (
      context: LogicContext,
      event: EventObject & { data: { path: string } }
    ) => {
      return {
        payload: event.data,
        type: 'UPDATE_PAGE',
      };
    }
  ),

  chooseGameModalCloseMethod: send((context: LogicContext) => {
    const strategy = getCloseStrategyByParams(
      context.gameToLaunch?.integration ?? '',
      context.gameCloseStrategies
    );

    return {
      type: shouldCloseExternally(strategy)
        ? 'VIEW_CLOSE_GAME_MODAL_THROUGH_GAME_FRAME'
        : 'VIEW_CLOSE_GAME_MODAL',
    };
  }),

  // ROUTING
  goBack: send('GLOBAL_GOBACK'),
  startTimer: () => console.time('TIME_TEST'),
  logTime: () => console.timeEnd('TIME_TEST'),

  // NAVIGATION
  nav_HANDLE_CHANGE: send((context: LogicContext, event: any) => ({
    payload: event.payload,
    type: 'NAV_HANDLE_CHANGE',
  })),

  // LAYOUT CHANGE
  layout_openSystemErrorPage: send('LAYOUT_SYSTEM_ERROR_PAGE_OPEN'),
  layout_changePageId: send((context: LogicContext, event: any) => {
    const isInNav =
      context.navigationLinks &&
      context.navigationLinks.some(link => link === context.currentPagePath);

    return {
      type: 'LAYOUT_PAGEID_CHANGE',
      mainLayoutPath: isInNav
        ? context.currentPagePath
        : context.mainLayoutPath,
      reload_layout: !event.type || event.data?.loggedIn === false,
      noScroll:
        event.type === 'LAYOUT_SIDEBAR_CLOSE' ||
        event.type === 'LAYOUT_MODAL_CLOSE' ||
        event.type === 'LAYOUT_GAME_MODAL_CLOSE' ||
        event.type === 'LAYOUT_SEARCH_MODAL_CLOSE' ||
        event.type === 'LAYOUT_VIDEO_MODAL_CLOSE',
      event,
    };
  }),
  layout_reload: send((context: LogicContext, event: any) => ({
    type: 'LAYOUT_RELOAD',
    reload_layout: !event.type || event.data?.loggedIn === false,
    event,
  })),
  layout_notifyChangeEnd: send((context: LogicContext, event: any) => {
    return {
      type: 'LAYOUT_CHANGE_END',
      controllersData: event.data?.controllersData,
      mainLayoutPath: event.data?.mainLayoutPath,
      pageTitle: event.data?.pageTitle,
    };
  }),
  layout_recalculate: send((context: LogicContext, event: any) => ({
    type: 'LAYOUT_RECALCULATE',
    data: event.data,
  })),
  layout_closeSidebar: send((context: LogicContext, event: any) => {
    return {
      type: 'LAYOUT_SIDEBAR_CLOSE',
      mainLayoutPath: event.data?.mainLayoutPath,
    };
  }),
  layout_closeModal: send('LAYOUT_MODAL_CLOSE'),
  layout_closeSearchModal: send('LAYOUT_SEARCH_MODAL_CLOSE'),
  layout_closeSearchModalTrigger: send('LAYOUT_SEARCH_MODAL_CLOSE_TRIGGER'),
  layout_closeGameModal: send('LAYOUT_GAME_MODAL_CLOSE'),
  layout_closeVideoModal: send('LAYOUT_VIDEO_MODAL_CLOSE'),
  layout_openGameModal: send('LAYOUT_GAME_MODAL_OPEN'),
  layout_openVideoModal: send('LAYOUT_VIDEO_MODAL_OPEN'),
  layout_exitChange: send('LAYOUT_EXIT_CHANGE'),

  // OPEN MODAL
  gameModal_open: send((context: LogicContext, event: any) => ({
    type: 'OPEN_GAME_MODAL',
    payload: {
      path: context.gameToLaunch?.gameLaunchPath,
    },
  })),
  // OPEN GAME DETAILS
  gameDetails_open: send((context: LogicContext, event: any) => ({
    type: 'OPEN_GAME_DETAILS',
    payload: {
      path: context.gameToLaunch?.gameDetailsPath,
    },
  })),
  // LAUNCH GAME
  gameLaunch_trigger: send('LAUNCH_GAME'),
  // TRACKING
  tracking_changePage: send('TRACK_PAGE_CHANGE'),
  // RECENTLY PLAYED
  rp_rerender: send('RP_RERENDER'),
  // BINGO GAMES
  bingo_games_rerender: send('BINGO_GAMES_RERENDER'),
  // PROMOTIONS
  promotions_updateUrl: send(
    (
      context: LogicContext,
      event: EventObject & { payload?: { pageSlug?: string } }
    ) => ({
      type: 'UPDATE_PROMOTIONS_URL',
      pageSlug: event.payload && event.payload.pageSlug,
    })
  ),
  // JACKPOTS TILES - Daily Jackpot, Must Drop Jackpots, Vegas Millions
  jackpotsComponents_reload: send(
    (
      context: LogicContext,
      event: EventObject & { data?: { jackpotCode?: string } }
    ) => ({
      type: 'RELOAD_JACKPOT_COMPONENT',
      jackpotCode: event.data && event.data?.jackpotCode,
    })
  ),
  // Component with Jackpot Feed: JackpotWidget, JackpotDrawer
  jackpotsFeedComponents_reload: send(
    (
      context: LogicContext,
      event: EventObject & { data?: unknown }
    ) => ({
      type: 'RELOAD_JACKPOTS_FEED_COMPONENT',
      data: event.data,
    })
  ),
  // CATEGORIES
  categories_reload: send('RELOAD_CATEGORIES'),
  categories_refetch: send('REFETCH_CATEGORIES'),
  // SEARCH
  clearSearch_trigger: send('CLEAR_SEARCH_PHRASE'),
  search_items_reload: send('RELOAD_SEARCH_ITEMS'),

  // LIVE PERSON CHAT
  cashierOpenLiveChat: () => {
    const livePersonChatConfig = config.features.livePersonChatListener;
    if (
      !livePersonChatConfig.name ||
      !livePersonChatConfig.redirectUrl ||
      !livePersonChatConfig.iosRedirectUrl
    ) {
      return;
    }
    LivePersonChatFrontend.activateEngagement(
      livePersonChatConfig.name,
      livePersonChatConfig.redirectUrl,
      livePersonChatConfig.iosRedirectUrl
    );
  },
};
